import { AsyncButton, colors, InputField } from '@one-tree/library'
import React, { ReactElement, useState } from 'react'
import styled from 'styled-components'
import { useBasket } from '../../context/BasketProvider'
import { useOrganisation } from '../../context/OrganisationProvider'
import { validatePromoCode } from '../../helpers/APIHelper'
import { canAcceptPromoCode } from '../../helpers/basketHelper'

const Styles = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`
const Inputs = styled.div`
  display: flex;
  align-items: end;
  column-gap: 10px;
  margin: 20px auto 0;
`
const Error = styled.p`
  color: ${colors.red};
`
const Success = styled.p`
  color: ${colors.green};
`
export default function PromoCode(): ReactElement | null {
  const { organisation, buttonColors } = useOrganisation()
  const { applyPromoCode, basketItems } = useBasket()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const showPromoCodeInput = canAcceptPromoCode(basketItems)

  const handleCode = async (): Promise<void> => {
    if (!organisation) return
    setError('')
    setSuccess('')

    const res = await validatePromoCode(organisation.id, code, basketItems)

    if (res.error || !res.data) {
      setError(res.error || 'No valid promotion found with that code')
      return
    }

    const { appliedToBasketItems = [], ...promotion } = res.data

    if (!appliedToBasketItems || appliedToBasketItems.length === 0) {
      setError('That code doesn\'t apply to any items in your basket')
      setCode('')
      return
    }

    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const promotionToApply = promotion as Parameters<typeof applyPromoCode>[0]
    const affected = applyPromoCode(promotionToApply, appliedToBasketItems)

    if (affected === 0) {
      setError('That code doesn\'t apply to any items in your basket')
      setCode('')
      return
    }

    setSuccess(
      `Promotion applied to ${affected} item${affected > 1 ? 's' : ''}`,
    )
    setCode('')
  }

  if (!showPromoCodeInput) return null

  return (
    <Styles>
      <Inputs>
        <InputField
          label="Apply a promotional code"
          value={code}
          onChange={setCode}
          isError={!!error}
        />
        <AsyncButton customColors={buttonColors} onClick={handleCode}>
          Apply
        </AsyncButton>
      </Inputs>
      {error && <Error>{error}</Error>}
      {success && <Success>{success}</Success>}
    </Styles>
  )
}
